import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Store, Clock, KeyRound, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { buildAdminApi, setAdminPin } from "../../lib/admin";

const inputCls =
    "w-full rounded-2xl border border-[#E8DCCB] bg-[#F9F6F0] px-4 py-3 text-sm focus:outline-none focus:border-[#FF4F18] transition-colors";
const btnCls =
    "inline-flex items-center gap-2 rounded-full bg-[#2C1A12] text-white px-5 py-3 text-sm font-display font-bold active:scale-95 transition-transform disabled:opacity-50";
const HOURS = Array.from({ length: 25 }, (_, h) => h);
const hourLabel = (h) => (h === 0 || h === 24 ? "12am" : h === 12 ? "12pm" : h < 12 ? `${h}am` : `${h - 12}pm`);
const TIMEZONES = ["Australia/Sydney", "Australia/Melbourne", "Australia/Brisbane", "Australia/Adelaide", "Australia/Perth", "Australia/Hobart", "Australia/Darwin", "Pacific/Auckland", "Asia/Kolkata", "Asia/Singapore", "Europe/London", "America/New_York", "America/Los_Angeles"];

const Section = ({ id, icon: Icon, title, sub, children }) => (
    <section id={id} data-testid={`settings-section-${id}`} className="scroll-mt-28 rounded-3xl bg-white border border-[#E8DCCB] p-6">
        <div className="flex items-start gap-3 mb-5">
            <div className="h-10 w-10 rounded-2xl bg-[#FF4F18]/10 text-[#FF4F18] flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <div>
                <h2 className="font-display font-black text-xl">{title}</h2>
                <p className="text-sm text-[#8C7A72]">{sub}</p>
            </div>
        </div>
        {children}
    </section>
);

const Field = ({ label, children }) => (
    <label className="block">
        <span className="block text-xs uppercase tracking-[0.2em] text-[#8C7A72] font-semibold mb-2">{label}</span>
        {children}
    </label>
);

const useSave = (api) => {
    const [busy, setBusy] = useState(false);
    const save = async (body, msg) => {
        setBusy(true);
        try {
            await api.patch("/admin/settings", body);
            toast.success(msg);
            return true;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
            return false;
        } finally {
            setBusy(false);
        }
    };
    return [busy, save];
};

const DetailsCard = ({ api, t }) => {
    const [f, setF] = useState({ name: t.name || "", contact_phone: t.contact_phone || "", address: t.address || "" });
    const [busy, save] = useSave(api);
    return (
        <Section id="details" icon={Store} title="Café details" sub="Shown to customers on your café page.">
            <div className="grid gap-4 md:grid-cols-2">
                <Field label="Café name"><input data-testid="settings-name-input" className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
                <Field label="Contact phone"><input data-testid="settings-phone-input" className={inputCls} value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} /></Field>
                <div className="md:col-span-2"><Field label="Address"><input data-testid="settings-address-input" className={inputCls} value={f.address} placeholder="12 King St, Sydney NSW" onChange={(e) => setF({ ...f, address: e.target.value })} /></Field></div>
            </div>
            <button data-testid="settings-details-save" disabled={busy || !f.name.trim()} onClick={() => save(f, "Café details saved")} className={`${btnCls} mt-5`}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save details
            </button>
        </Section>
    );
};

const HoursCard = ({ api, t }) => {
    const [f, setF] = useState({
        always_open: t.always_open ?? true,
        open_hour_start: t.open_hour_start ?? 7,
        open_hour_end: t.open_hour_end ?? 15,
        timezone: t.timezone || "Australia/Sydney",
    });
    const [busy, save] = useSave(api);
    const invalid = !f.always_open && f.open_hour_start >= f.open_hour_end;
    const tzs = TIMEZONES.includes(f.timezone) ? TIMEZONES : [f.timezone, ...TIMEZONES];
    return (
        <Section id="hours" icon={Clock} title="Trading hours" sub="Customers can only pre-order while you're open.">
            <label className="flex items-center gap-3 mb-5 cursor-pointer">
                <input data-testid="settings-always-open" type="checkbox" className="h-5 w-5 accent-[#FF4F18]" checked={f.always_open} onChange={(e) => setF({ ...f, always_open: e.target.checked })} />
                <span className="font-display font-bold text-sm">Accept orders 24/7</span>
            </label>
            <div className="grid gap-4 md:grid-cols-3">
                <Field label="Opens">
                    <select data-testid="settings-open-hour" disabled={f.always_open} className={inputCls} value={f.open_hour_start} onChange={(e) => setF({ ...f, open_hour_start: +e.target.value })}>
                        {HOURS.slice(0, 24).map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                    </select>
                </Field>
                <Field label="Closes">
                    <select data-testid="settings-close-hour" disabled={f.always_open} className={inputCls} value={f.open_hour_end} onChange={(e) => setF({ ...f, open_hour_end: +e.target.value })}>
                        {HOURS.slice(1).map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                    </select>
                </Field>
                <Field label="Timezone">
                    <select data-testid="settings-timezone" className={inputCls} value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })}>
                        {tzs.map((z) => <option key={z} value={z}>{z}</option>)}
                    </select>
                </Field>
            </div>
            {invalid && <p data-testid="settings-hours-error" className="mt-3 text-sm text-[#c73810]">Closing time must be after opening time.</p>}
            <button data-testid="settings-hours-save" disabled={busy || invalid} onClick={() => save(f, "Trading hours saved")} className={`${btnCls} mt-5`}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save hours
            </button>
        </Section>
    );
};

const PinCard = ({ api, slug }) => {
    const [pin, setPin] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, save] = useSave(api);
    const valid = /^\d{4,8}$/.test(pin) && pin === confirm;
    const submit = async () => {
        if (await save({ admin_pin: pin }, "Admin PIN updated")) {
            setAdminPin(slug, pin);
            setPin("");
            setConfirm("");
        }
    };
    return (
        <Section id="pin" icon={KeyRound} title="Admin PIN" sub="4-8 digits. Share only with staff you trust.">
            <div className="grid gap-4 md:grid-cols-2">
                <Field label="New PIN"><input data-testid="settings-pin-input" type="password" inputMode="numeric" maxLength={8} className={inputCls} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} /></Field>
                <Field label="Confirm PIN"><input data-testid="settings-pin-confirm" type="password" inputMode="numeric" maxLength={8} className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))} /></Field>
            </div>
            {confirm && pin !== confirm && <p className="mt-3 text-sm text-[#c73810]">PINs don't match.</p>}
            <button data-testid="settings-pin-save" disabled={busy || !valid} onClick={submit} className={`${btnCls} mt-5`}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update PIN
            </button>
        </Section>
    );
};

const BillingCard = ({ api }) => {
    const [sub, setSub] = useState(null);
    const [loadErr, setLoadErr] = useState(false);
    const [busy, setBusy] = useState(null);
    useEffect(() => { api.get("/subscription").then((r) => setSub(r.data)).catch(() => setLoadErr(true)); }, [api]);
    useEffect(() => {
        const q = new URLSearchParams(window.location.search).get("subscribed");
        if (q === "1") toast.success("You're subscribed — thanks for backing Coffeein7!");
        if (q === "0") toast("Checkout cancelled — no charge made.");
    }, []);
    const go = async (key, path, body, urlKey) => {
        setBusy(key);
        try {
            const r = await api.post(path, { ...body, origin_url: window.location.origin });
            if (r.data?.[urlKey]) window.location.assign(r.data[urlKey]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't open billing");
            setBusy(null);
        }
    };
    if (loadErr) return <Section id="billing" icon={CreditCard} title="Billing" sub="Couldn't load billing. Refresh to try again, or email admin@coffeein7.com."><div data-testid="settings-billing-error" /></Section>;
    if (!sub) return <Section id="billing" icon={CreditCard} title="Billing" sub="Loading…"><div /></Section>;
    const active = sub.subscription_status === "active";
    const tier = sub.subscription_tier === "pro" ? "Pro" : "Starter";
    const status = active ? `${tier} plan · Active` : sub.trial_days_left > 0 ? `Free trial · ${sub.trial_days_left} days left` : "Trial ended — choose a plan";
    return (
        <Section id="billing" icon={CreditCard} title="Billing" sub="Plans, card details and invoices.">
            <div data-testid="settings-billing-status" className="rounded-2xl bg-[#F9F6F0] px-4 py-3 font-display font-bold text-sm mb-5">{status}</div>
            <div className="flex flex-wrap gap-2">
                {sub.has_stripe_customer && (
                    <button data-testid="settings-manage-billing" disabled={!!busy} onClick={() => go("portal", "/subscription/portal", {}, "portal_url")} className={btnCls}>
                        {busy === "portal" && <Loader2 className="h-4 w-4 animate-spin" />} Manage billing & invoices
                    </button>
                )}
                {!active && ["starter", "pro"].map((t) => (
                    <button key={t} data-testid={`settings-plan-${t}`} disabled={!!busy} onClick={() => go(t, "/subscription/checkout", { tier: t }, "checkout_url")}
                        className={t === "pro" ? `${btnCls} !bg-[#FF4F18]` : `${btnCls} !bg-white !text-[#2C1A12] border-2 border-[#2C1A12]`}>
                        {busy === t && <Loader2 className="h-4 w-4 animate-spin" />} {t === "pro" ? "Pro · $79/mo" : "Starter · $49/mo"}
                    </button>
                ))}
            </div>
            {!active && sub.trial_days_left > 0 && (
                <p className="mt-3 text-xs text-[#8C7A72]">Add a card now — you won't be charged until your free trial ends.</p>
            )}
        </Section>
    );
};

const AdminSettings = () => {
    const { slug } = useParams();
    const { hash } = useLocation();
    const api = useMemo(() => buildAdminApi(slug), [slug]);
    const [tenant, setTenant] = useState(null);

    useEffect(() => { api.get("/admin/settings").then((r) => setTenant(r.data)).catch(() => toast.error("Couldn't load settings")); }, [api]);
    useEffect(() => {
        if (tenant && hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [tenant, hash]);

    return (
        <div data-testid="admin-settings-page" className="space-y-6">
            <h1 className="font-display font-black text-3xl">Settings</h1>
            {!tenant ? <Loader2 className="h-6 w-6 animate-spin text-[#8C7A72]" /> : (
                <>
                    <DetailsCard api={api} t={tenant} />
                    <HoursCard api={api} t={tenant} />
                    <PinCard api={api} slug={slug} />
                    <BillingCard api={api} />
                </>
            )}
        </div>
    );
};

export default AdminSettings;
